import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import DateRangeIcon from "@mui/icons-material/DateRange";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.css";
import "../../../assets/scss/datepicker.css";
import { customGetAPI, customAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { canonicalLeaveStatus } from "pages/TeamZone/Approve/Leave/LeaveStatusCards";
import { showToast } from "services/utils/Status";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { designColors, designRadii } from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";

const SummaryRow = ({ label, value }) => (
  <Mui.Stack direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
    <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600 }}>
      {label}
    </Mui.Typography>
    <Mui.Typography sx={{ fontSize: 13.5, fontWeight: 600, color: designColors.slate900 }}>
      {value ?? "N/A"}
    </Mui.Typography>
  </Mui.Stack>
);

// Cancel-leave confirmation, now a Dialog opened from within
// LeaveBalanceDetailPanel's Summary tab instead of its own full-page route —
// the "which dates to cancel" business-day validation and submit logic below
// is unchanged from the previous full-page version, only the surrounding
// chrome (BackArrow + full-page form) was swapped for a Dialog.
export const CancelLeaveDialog = ({
  open,
  onClose,
  cancelLeaveData,
  leaveYearId,
  onCancelled,
  // LeaveBalanceDetailPanel (Team Workspace) opens this for a manager
  // cancelling someone else's leave, gated on the "Leave Balances" editor
  // permission plus the list API's is_cancel_enabled flag. MyZone's
  // ViewLeave (My Workspace) reuses this same dialog for an employee
  // cancelling their OWN leave — neither of those Team Workspace-only
  // signals is ever granted/set for that self-service case, which made the
  // dialog silently refuse to open there. Callers outside Team Workspace
  // pass requireEditorPermission={false} to skip them and rely on their own
  // access gate instead (see ViewLeave's checkAccess).
  requireEditorPermission = true,
}) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm();
  const { domain, userScreenAccess } = useSelector((state) => state.tokenReducer);
  // The Approved-status + is_cancel_enabled + editor-permission gate below
  // only makes sense for the Team Workspace flow (cancelling someone else's
  // already-approved leave). MyZone's self-service flow passes
  // requireEditorPermission={false} because it cancels a still-Pending
  // leave via its own checkAccess gate (see ViewLeave) — so all three of
  // those checks must be skipped together, not just the permission one.
  const canCancel =
    !requireEditorPermission ||
    (checkEditorPermission(
      userScreenAccess?.module_details,
      "Leave Balances",
      "Leave Balances",
    ) &&
      Boolean(cancelLeaveData?.is_cancel_enabled) &&
      canonicalLeaveStatus(cancelLeaveData?.status) === "Approved");
  const [triggerApi, setTriggerApi] = useState(true);
  const [applyLeaveDates, setApplyLeaveDates] = useState({});
  const [getLeaveDates, setGetLeaveDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [state, setState] = useState({
    start: moment(cancelLeaveData?.from_date, "DD-MM-YYYY"),
    end: moment(cancelLeaveData?.to_date, "DD-MM-YYYY"),
  });

  const buildDateList = (rangeStart, rangeEnd) => {
    const dates = [];
    const currentDate = moment(rangeStart);
    while (currentDate.isSameOrBefore(rangeEnd, "day")) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
    return dates;
  };

  useEffect(() => {
    if (!open) return;
    setTriggerApi(true);
    reset();
    const nextStart = moment(cancelLeaveData?.from_date, "DD-MM-YYYY");
    const nextEnd = moment(cancelLeaveData?.to_date, "DD-MM-YYYY");
    setState({ start: nextStart, end: nextEnd });
    // Pre-fill with the full originally-approved range, same as what the
    // date field displays — otherwise a user who submits without touching
    // the picker (the common case for a single-day leave) sends an empty
    // invalid_dates/selectedDates even though the field shows a date.
    setSelectedDates(buildDateList(nextStart, nextEnd));
  }, [open, cancelLeaveData, reset]);

  const { start, end } = state;

  const handleCallback = (nextStart, nextEnd) => {
    setState({ start: nextStart, end: nextEnd });
    setSelectedDates(buildDateList(nextStart, nextEnd));
  };

  const label = `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`;

  useEffect(() => {
    const fetchLeaveDetails = async () => {
      const res = await customGetAPI(
        `${domain}valid_business_days/?leave_request_id=${cancelLeaveData?.id}`,
      );
      if (res.ok) {
        const dateValidation = res?.responseDetails?.date_validation || {};
        const validDates = Object.entries(dateValidation)
          .filter(([, status]) => status === "valid")
          .map(([date]) => date);
        setGetLeaveDates(validDates);
        setApplyLeaveDates(dateValidation);
      }
    };
    if (open && canCancel && triggerApi && cancelLeaveData?.id) {
      fetchLeaveDetails();
      setTriggerApi(false);
    }
  }, [open, canCancel, cancelLeaveData?.id, domain, triggerApi]);

  const validCancelledCount = selectedDates.filter(
    (date) => applyLeaveDates[date] === "valid",
  ).length;

  const netLeave = cancelLeaveData?.is_half_day
    ? (cancelLeaveData?.business_days || 0) - 0.5
    : (cancelLeaveData?.business_days || 0) - validCancelledCount;

  const onSubmit = async (data) => {
    if (!canCancel) return;
    setButtonLoader(true);
    const payload = {
      leave_request_id: cancelLeaveData?.id,
      date_validation: applyLeaveDates,
      invalid_dates: selectedDates,
      leave_year_id: leaveYearId,
      reason: data.reason,
    };
    const response = await customAPI(
      `${domain}cancel_create_leave_request/`,
      "PUT",
      payload,
    );
    setButtonLoader(false);
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      onCancelled?.();
    }
  };

  return (
    <Mui.Dialog
      open={open && canCancel}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: designRadii.card } }}
    >
      <Mui.Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Mui.DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate900 }}>
          Cancel Leave
        </Mui.DialogTitle>
        <Mui.DialogContent>
          <Mui.Stack spacing={0.25} sx={{ pb: 1, borderBottom: `1px solid ${designColors.slate100}` }}>
            <SummaryRow
              label="Leave Dates"
              value={`${cancelLeaveData?.from_date || "N/A"} - ${cancelLeaveData?.to_date || "N/A"}`}
            />
            <SummaryRow label="Business Days" value={cancelLeaveData?.business_days} />
          </Mui.Stack>

          <Mui.Box sx={{ mt: 1.75 }}>
            <Mui.Typography sx={{ fontSize: 13, fontWeight: 600, color: designColors.slate700, mb: 0.5 }}>
              Dates to be cancelled
            </Mui.Typography>
            <Mui.Typography sx={{ fontSize: 12, color: designColors.slate500, mb: 0.75 }}>
              Choose the dates you want to cancel from the originally approved leave.
            </Mui.Typography>
            {Array.isArray(getLeaveDates) && getLeaveDates.length > 0 ? (
              <DateRangePicker
                initialSettings={{
                  startDate: start.toDate(),
                  endDate: end.toDate(),
                  applyClass: "custom-apply-btn",
                  cancelClass: "custom-cancel-btn",
                  minDate: moment(cancelLeaveData?.from_date, "DD-MM-YYYY"),
                  maxDate: moment(cancelLeaveData?.to_date, "DD-MM-YYYY"),
                  locale: { format: "DD/MM/YYYY" },
                  isInvalidDate: (date) =>
                    !getLeaveDates.includes(date.format("YYYY-MM-DD")),
                }}
                onCallback={handleCallback}
              >
                <Mui.TextField
                  fullWidth
                  size="small"
                  value={label}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Mui.InputAdornment position="end">
                        <DateRangeIcon sx={{ color: designColors.slate400 }} />
                      </Mui.InputAdornment>
                    ),
                  }}
                  sx={globalTextFieldSx}
                />
              </DateRangePicker>
            ) : null}
          </Mui.Box>

          <Mui.Stack spacing={0.25} sx={{ mt: 1.75, pb: 1, borderBottom: `1px solid ${designColors.slate100}` }}>
            <SummaryRow
              label="Days to be cancelled"
              value={cancelLeaveData?.is_half_day ? 0.5 : validCancelledCount}
            />
            <SummaryRow label="Net Leave" value={netLeave} />
          </Mui.Stack>

          <Mui.Box sx={{ mt: 1.75 }}>
            <Mui.Typography sx={{ fontSize: 13, fontWeight: 600, color: designColors.slate700, mb: 0.5 }}>
              Reason
            </Mui.Typography>
            <Mui.TextField
              fullWidth
              multiline
              minRows={2}
              placeholder="Enter a reason for cancelling this leave"
              {...register("reason", {
                required: "Reason for cancel leave is required",
                validate: {
                  minLengthNoSpaces: (value) => {
                    if (value?.trim().length < 3 && value !== "") {
                      return minimum3Characters;
                    }
                    return true;
                  },
                },
              })}
              error={Boolean(errors.reason)}
              helperText={errors.reason?.message}
              sx={globalTextFieldSx}
            />
          </Mui.Box>
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <SecondaryButton type="button" onClick={onClose}>
            Close
          </SecondaryButton>
          <PrimaryButton type="submit" loading={buttonLoader}>
            Update
          </PrimaryButton>
        </Mui.DialogActions>
      </Mui.Box>
    </Mui.Dialog>
  );
};
